<script setup>
import { onMounted, ref, nextTick, onBeforeUnmount, computed } from "vue";
import { useTopicStore } from "@/stores/useTopicStore";
import CustomTextarea from "../Generics/CustomTextarea.vue";
import { toTitleCase } from "@/utils/stringUtils";

import { useDraggableModal } from "@/composables/useDraggableModal";

const topicStore = useTopicStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const obj = ref({
  topicId: 0,
  topicName: "",
  description: "",
});

const nameInput = ref(null);
const isSubmitting = ref(false);

const emit = defineEmits(["close", "refresh"]);

// ====================================================================
// ✅ TOAST PADRÃO ADDTOPICFORM.VUE (3000ms) 🚀
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao salvar com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ====================================================================

const isSubmitDisabled = computed(() => {
  const name = obj.value.topicName || "";
  return isSubmitting.value || name.trim().length === 0;
});

async function edit() {
  if (isSubmitting.value) return;

  if (!obj.value.topicName || !obj.value.topicName.trim()) {
    showErrorToast("Informe o nome do assunto.");
    return;
  }

  isSubmitting.value = true;

  try {
    obj.value.topicName = toTitleCase(
      (obj.value.topicName ?? "").toString().trim().replace(/\s+/g, " ")
    );

    await topicStore.updateTopic(obj.value);

    showSuccessToast("Assunto atualizado com sucesso.");
    hideBackdropForSuccess.value = true;

    setTimeout(() => {
      isSubmitting.value = false;
      emit("refresh");
      emit("close");
    }, 2400);
  } catch (error) {
    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao salvar as alterações.";
    const errorData = error?.response?.data || error;

    if (errorData && errorData.errors) {
      const messages = Object.values(errorData.errors).flat();
      if (messages.length > 0) {
        userFriendlyMessage = messages.join("\n");
      }
    } else if (errorData && errorData.message) {
      userFriendlyMessage = errorData.message;
    } else if (error.message && typeof error.message === "string") {
      userFriendlyMessage = error.message;
    }

    console.error("Erro detalhado da API ao salvar:", errorData);

    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

function closeModal() {
  if (isSubmitting.value) return;
  emit("close");
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

onMounted(async () => {
  try {
    const topicData = await topicStore.getTopicById(topicStore.idTopic);
    obj.value = { ...topicData };
  } catch (err) {
    console.error("Erro ao carregar assunto:", err.message);
    showErrorToast("Erro ao carregar os dados do assunto.");
  }

  nextTick(() => {
    if (nameInput.value) nameInput.value.focus();
  });

  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }
});
</script>

<template>
  <div
    v-show="!hideBackdropForSuccess"
    class="modal-backdrop"
    @click.self="!isSubmitting"
  >
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
          'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Editar Assunto</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="edit" novalidate>
        <div class="modal-body">
          <div class="form-content">
            <div class="form-group">
              <label for="name" class="form-label">Nome</label>
              <input
                ref="nameInput"
                type="text"
                id="name"
                v-model="obj.topicName"
                @blur="obj.topicName = toTitleCase(obj.topicName)"
                class="input-base"
                placeholder="Digite o nome do assunto"
                required
                @focus="$event.target.select()"
              />
            </div>

            <div class="form-group">
              <label for="description" class="form-label"
                >Descrição (opcional)</label
              >
              <CustomTextarea
                v-model:body="obj.description"
                placeholder="Digite uma descrição (opcional)"
                :is-disabled="false"
                style="min-height: 120px"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitDisabled"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                clip-rule="evenodd"
              />
            </svg>

            <span>
              {{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- ✅ TOAST IDÊNTICO AO AddTopicForm.vue -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none flex items-start justify-center pt-6 px-4"
        style="z-index: 9999999"
      >
        <div
          :class="[
            'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* =========================
   MODAL
========================= */
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background-color: rgba(0, 0, 0, 0.2);

  display: flex;
  justify-content: center;
  align-items: flex-start;

  padding: 1.25rem;
  overflow-y: auto;
}

.modal-panel {
  width: 100%;
  max-width: 32rem;
  background-color: var(--bg-content);
  border-radius: 16px;
  overflow: hidden;

  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2.5rem);
}

.modal-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

/* 🔹 NOVO: cabeçalho como alça de arrasto do painel.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.modal-header--dragging {
  cursor: grabbing;
}
/* O botão de fechar continua sendo clique, não alça */
.modal-header--draggable .modal-close-btn {
  cursor: pointer;
}

.modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.modal-close-btn {
  background: none;
  border: none;
  cursor: pointer;

  padding: 0.35rem;
  border-radius: 10px;
  color: #94a3b8;
  transition: all 0.2s ease-in-out;
}

.modal-close-btn:hover:enabled {
  background-color: #e2e8f0;
  color: #1e293b;
}

.modal-close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 1rem 1.5rem;
  overflow-y: auto;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.modal-footer {
  padding: 0.75rem 1.5rem;
  border-top: 1px solid var(--border-color, #eee);  
  backdrop-filter: blur(5px);

  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  background-color: var(--bg-header);
}

/* =========================
   FORM
========================= */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-dark);
}

.input-base {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--border-color);
  background-color: white;
  color: var(--text-dark);

  font-size: 0.9rem;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input-base:focus {
  outline: none;
  border-color: #a0c4ff;
  box-shadow: 0 0 0 3px rgba(160, 196, 255, 0.4);
}

.input-base:disabled {
  background-color: var(--bg-header);
  cursor: not-allowed;
}

/* =========================
   BUTTONS
========================= */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 0.9rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  border: 1px solid transparent;
  white-space: nowrap;
}
.btn:focus,
.btn:focus-visible {
  outline: none;
  box-shadow: none;
}

/* ── btn-primary (Avançar) — mesmo visual do Voltar ── */
.btn-primary {
  background-color: #e2e8f0;
  color: #1f2937;
  border-color: #cbd5e1;
}
.btn-primary:hover:not(:disabled) {
  background-color: #d7dde5;
}
.btn-primary:active:not(:disabled) {
  transform: translateY(1px);
}

/* ── btn-secondary (Voltar) ── */
.btn-secondary {
  background-color: #e2e8f0;
  color: #1f2937;
  border-color: #cbd5e1;
}
.btn-secondary:hover:not(:disabled) {
  background-color: #d7dde5;
}
.btn-secondary:active:not(:disabled) {
  transform: translateY(1px);
}

/* ── btn-success (Salvar/Adicionar) ── */
.btn-success {
  background-color: #9b84ff;
  color: #1a2238;
}
.btn-success:hover:not(:disabled) {
  background-color: #8a73f5;
}
.btn-success:focus,
.btn-success:focus-visible {
  outline: none;
  box-shadow: none;
}
.btn-success:active:not(:disabled) {
  transform: translateY(1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ====================================================================
   ✅ PATCH: torna o toast referência para posicionar o botão fechar
   ==================================================================== */
/* O padding-right foi aumentado para reservar espaço para o "x" no canto
   superior direito, sem sobrepor o texto do toast. */
.toast {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  max-width: 90vw;
  padding: 1rem 3.25rem 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: toastSlideDown 0.4s ease forwards;
}

@keyframes toastSlideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.35s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.toast-icon-circle {
  stroke-width: 2;
}

.toast-title {
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.toast-message {
  font-size: 1rem;
  opacity: 0.9;
}

/* ====================================================================
   ✅ PATCH: fixa o botão "x" no canto superior direito do toast
   ==================================================================== */
/* Antes o botão participava do fluxo do flex e ficava logo após o texto.
   Agora ele fica absoluto dentro do card do toast. */
.toast-close {
  position: absolute;
  top: 0.65rem;
  right: 0.75rem;

  width: 1.75rem;
  height: 1.75rem;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  line-height: 1;
  color: inherit;
}

.toast-error {
  background-color: #fee2e2;
  border-left: 6px solid #ef4444;
  color: #991b1b;
}

.toast-error .toast-icon-circle {
  fill: #fee2e2;
  stroke: #ef4444;
}

.toast-success {
  background-color: #dcfce7;
  border-left: 6px solid #22c55e;
  color: #166534;
}

.toast-success .toast-icon-circle {
  fill: #dcfce7;
  stroke: #22c55e;
}
</style>